import React from 'react';

import {Button, Checkbox, Icon, Menu, Text} from '@albert-io/atomic';
import {useMenu} from '@albert-io/atomic/organisms/Menu/MenuContext.react';

import type {SelectOption} from '../types';

/** A labelled group of single-select filter options rendered as check-marked menu items. */
export const FilterSection = <T extends string>({
  label,
  options,
  selected,
  onSelect
}: {
  label: string;
  options: ReadonlyArray<SelectOption<T>>;
  selected: T;
  onSelect: (value: T) => void;
}) => (
  <>
    <Menu.Item readonly textProps={{color: 'secondary', size: 's'}}>
      {label}
    </Menu.Item>
    {options.map((option) => (
      <Menu.Item
        key={option.value}
        selected={selected === option.value}
        rightIconProps={selected === option.value ? {icon: 'check'} : undefined}
        onClick={() => onSelect(option.value)}
      >
        {option.label}
      </Menu.Item>
    ))}
  </>
);

/**
 * A labelled group of filter options as checkboxes. Any number can be ticked; none ticked means no
 * filter.
 *
 * Plain rows, not Menu.Items: Menu.Item calls preventDefault on click, which would stop a nested
 * checkbox from ticking. Each box repeats its section name because a `role='menu'` prunes the
 * heading from the accessibility tree.
 */
export const CheckboxFilterSection = <T extends string>({
  label,
  options,
  selected,
  onChange,
  getTestId
}: {
  label: string;
  options: ReadonlyArray<SelectOption<T>>;
  selected: ReadonlyArray<T>;
  onChange: (selected: ReadonlyArray<T>) => void;
  getTestId?: (value: T) => string;
}) => (
  <div className='gap-x1-half flex flex-col'>
    <Text aria-hidden size='s'>
      {label}
    </Text>
    {options.map((option) => {
      const testId = getTestId?.(option.value);
      return (
        <label className='gap-x1 flex cursor-pointer items-start' key={option.value}>
          <Checkbox
            checked={selected.includes(option.value)}
            label={`${label} ${option.label}`}
            role='menuitemcheckbox'
            size='s'
            data-testid={testId}
            onChange={() =>
              onChange(
                selected.includes(option.value)
                  ? selected.filter((value) => value !== option.value)
                  : [...selected, option.value]
              )
            }
          />
          <Text aria-hidden>{option.label}</Text>
        </label>
      );
    })}
  </div>
);

/**
 * The tray body for checkbox and input sections, split out so it can reach the menu context to
 * close itself. Its `p-x1` doubles the menu's own padding to the design's 16px inset.
 */
export const FiltersMenuPanel = ({children}: {children: React.ReactNode}) => {
  const {close} = useMenu();
  return (
    // Keys stay native for the boxes and date segments. Escape, and Shift+Tab off the first box,
    // reach Menu.Content, which closes the menu and refocuses the trigger.
    <div
      className='gap-x3 p-x1 flex flex-col'
      onBlur={(event) => {
        // Close once focus leaves the menu. The trigger counts as inside, else clicking it closes
        // here and its own click reopens; clicking bare tray blurs to nothing (no relatedTarget).
        const menuRoot = event.currentTarget.closest('.o-menu');
        const next = event.relatedTarget as Node | null;
        if (next && menuRoot && !menuRoot.contains(next)) {
          close();
        }
      }}
      onKeyDown={(event) => {
        const backOutOfFirstBox =
          event.key === 'Tab' &&
          event.shiftKey &&
          event.target === event.currentTarget.querySelector('input');
        if (event.key !== 'Escape' && !backOutOfFirstBox) {
          event.stopPropagation();
        }
      }}
    >
      {children}
    </div>
  );
};

interface FiltersMenuShellProps {
  /** Drives the brand-colored icon/label and the active dot on the trigger. */
  isActive: boolean;
  // Forwarded to the trigger button. Named `data-testid` (not `testId`) so callers write the
  // literal the e2e-selector removal guard scans for, rather than a dynamic value it can't see.
  'data-testid': string;
  disabled?: boolean;
  /** The filter sections (FilterSection / CheckboxFilterSection / dividers / inline inputs). */
  children: React.ReactNode;
}

/**
 * The shared "Filters" trigger + dropdown shell used by the teacher classwork lists. It owns the
 * trigger button (icon + label + active dot) and the Menu; callers provide the sections as children.
 */
export const FiltersMenuShell = ({
  isActive,
  'data-testid': dataTestId,
  disabled,
  children
}: FiltersMenuShellProps) => (
  <Menu position='bottom-end' positionFixed>
    <Menu.Trigger>
      {(triggerProps) => (
        <Button
          {...triggerProps}
          color='secondary'
          disabled={disabled}
          variant='outlined'
          data-testid={dataTestId}
        >
          <Icon className='mr-x1' color={isActive ? 'brand' : undefined} icon='bars-filter' />
          <Text color={isActive ? 'brand' : 'inherit'}>Filters</Text>
          {isActive && (
            <span aria-hidden className='ml-x1 h-x1 w-x1 background-brand rounded-full' />
          )}
        </Button>
      )}
    </Menu.Trigger>
    <Menu.Content>{children}</Menu.Content>
  </Menu>
);
